<script>
  import { useQueryClient } from '@tanstack/svelte-query';
  import { client } from './lib/client';
  import cls from 'classnames';
  import {
    Button,
    Card,
    Input,
    Label,
    Modal,
    Listgroup,
    ListgroupItem,
  } from 'flowbite-svelte';
  import { PlusSolid, StarHalfStrokeSolid } from 'flowbite-svelte-icons';
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { sendLinkCreatedDebugEvent } from './debug';
  import StickyHelp from './StickyHelp.svelte';
  import Tags from './Tags.svelte';
  import { debounce } from './utils';
  import { createValidationState, outlineClasses } from './validation';
  import { slide } from 'svelte/transition';
  import Heading from './Heading.svelte';
  import { queryUserData } from './data';

  export let isShowingHelpModal;
  const QUERY_KEY_SHORTLINKS = 'shortlinks';

  const queryClient = useQueryClient();

  const ValidationState = {
    ...createValidationState(),
    isValid: false,
  };

  const urlPlaceholder = $_('CreateShortlink.urlPLaceholder', {
    default: 'Paste URL to be shortened',
  });
  const namePlaceholder = $_('CreateShortlink.namePlaceholder', {
    default: 'Name shortlink',
  });

  let url = '';
  let urlInputElement;
  let urlValidationState = ValidationState;

  const creatorConnectionsCampaignState = {
    showInlineDialog: false,
    loading: false,
    detectedCreatorConnections: false,
    suggestedCampaign: null,
    userProvidedCampaignId: null,
    showMoreDetails: false,
    userCampaignDecision: null,
  };

  let name = '';
  let nameInputElement;
  let nameValidationState = ValidationState;

  let tags = [];

  /**
   * URL validation is run when input pauses for 1 second and the value is a url.
   */
  let inputTimeoutId;
  const validationInterval = 1000;

  let isCreatingShortlink = false;

  export let newlyCreatedShortlinkName = '';

  $: {
    if (newlyCreatedShortlinkName === '') {
      isCreatingShortlink = false;
    }
  }

  $: userQuery = queryUserData();
  $: ccEnabled = $userQuery.data?.object?.ccEnabled;

  onMount(() => {
    urlInputElement = document.getElementById('url-input');
    nameInputElement = document.getElementById('name-input');
  });

  function clearForm() {
    url = '';
    urlInputElement.value = '';
    urlInputElement.blur();
    urlValidationState = ValidationState;

    name = '';
    nameInputElement.value = '';
    nameInputElement.blur();
    nameValidationState = ValidationState;

    creatorConnectionsCampaignState.showInlineDialog = false;
    creatorConnectionsCampaignState.message = '';

    tags = [];
  }

  function onUrlInput() {
    urlValidationState = ValidationState;

    creatorConnectionsCampaignState.showInlineDialog = false;
    creatorConnectionsCampaignState.campaignRequiresOptIn = false;
    creatorConnectionsCampaignState.message = '';
    creatorConnectionsCampaignState.suggestedCampaign = null;

    const onInputPaused = async () => {
      await validateUrl(url);
    };

    if (
      url.length >= 5 &&
      (url.substring(0, 5) === 'http:' || url.substring(0, 5) !== 'https')
    ) {
      url = `https://${url.replace(/^h?t?t?p?:?\/?\/?/i, '')}`;
    }

    if (urlInputElement.checkValidity()) {
      clearTimeout(inputTimeoutId);
      inputTimeoutId = setTimeout(onInputPaused, validationInterval);
    }
  }

  function onUrlBlur() {
    if (!urlValidationState.isValid && urlInputElement.checkValidity()) {
      clearTimeout(inputTimeoutId);
      setTimeout(async () => {
        await validateUrl(url);
      }, 100);
    }
  }

  function onNameInput() {
    nameValidationState = {
      ...ValidationState,
      ...{ isValid: name.length > 0 },
    };
  }

  function onUrlValidationError(error) {
    urlValidationState = {
      isValid: false,
      message: displayError(error.message),
      inputClass: outlineClasses.red,
      outputClass:
        'visible text-btnInputError pl-6 bg-[url(/images/error.svg)] bg-no-repeat',
    };
  }

  function onUrlValidationSuccess() {
    urlValidationState = {
      isValid: true,
      message: $_('CreateShortlink.validations.valid', {
        default: 'URL is correct, you can give it a name now and create.',
      }),
      inputClass: outlineClasses.green,
      outputClass:
        'visible text-btnInputSuccess pl-6 bg-[url(/images/success.svg)] bg-no-repeat',
    };
  }

  function onNameValidationError(error) {
    nameValidationState = {
      isValid: false,
      message: displayError(error.message),
      inputClass: outlineClasses.red,
      outputClass:
        'visible text-btnInputError pl-6 bg-[url(/images/error.svg)] bg-no-repeat',
    };
  }

  function onCreateLinkError(error) {
    isCreatingShortlink = false;
    if (error.message.includes('name')) {
      onNameValidationError(error);
    } else {
      onUrlValidationError(error);
    }
  }

  function scrollToShortlinks() {
    const container = document.getElementById('content');
    const shortlinks = document.getElementById('shortlinks');
    if (container && shortlinks) {
      shortlinks.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  async function onCreateLinkSuccess(json) {
    await queryClient.invalidateQueries([QUERY_KEY_SHORTLINKS]);
    scrollToShortlinks();
    clearForm();
    newlyCreatedShortlinkName = json.object.name;
    // The table refresh will reset this state
    // but just in case, we clean up after a delay.
    setTimeout(() => {
      isCreatingShortlink = false;
      newlyCreatedShortlinkName = '';
    }, 1500);
  }

  async function onCreateLink() {
    isCreatingShortlink = true;
    try {
      const response = await client.post('/api/create-shortlink', {
        name,
        url,
        tags,
        ...(ccEnabled &&
        (!!creatorConnectionsCampaignState.suggestedCampaign ||
          !!creatorConnectionsCampaignState.userProvidedCampaignId)
          ? {
              userCampaignDecision:
                creatorConnectionsCampaignState.userCampaignDecision,
              externalCampaignId:
                !creatorConnectionsCampaignState?.suggestedCampaign
                  ?.creatorCampaignStatus &&
                !creatorConnectionsCampaignState?.userCampaignDecision
                  ? undefined
                  : creatorConnectionsCampaignState?.suggestedCampaign
                      ?.campaignId,
              userProvidedCampaignId:
                creatorConnectionsCampaignState?.userProvidedCampaignId,
              debugAction: 'create-shortlink',
            }
          : {}),
      });

      if (response.status === 201) {
        sendLinkCreatedDebugEvent(response.data.object);
        onCreateLinkSuccess(response.data);
      }
    } catch (error) {
      console.error(error);
      isCreatingShortlink = false;
      if (error?.response?.status === 400) {
        onCreateLinkError(error?.response?.data?.error);
      }
    }
  }

  async function getCampaignForProductUrl(productUrl) {
    const ccResponse = await client.get(
      `/api/get-cc-campaigns?url=${encodeURIComponent(productUrl)}`,
    );
    return ccResponse;
  }

  const isValidAmazonShortlinkDomain = (url) => {
    try {
      const urlObj = new URL(url);
      const cleanDomain = urlObj.hostname.replace('www.', '');
      const SUPPORTED_AMAZON_SHORTLINK_DOMAIN = new Set(['amzn.to', 'a.co']);
      if (SUPPORTED_AMAZON_SHORTLINK_DOMAIN.has(cleanDomain)) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  function appendCampaignToUrl(campaignId) {
    const urlObj = new URL(url);
    // do not append campaign ids when the input is a shortlink or if campaignId is already present
    if (
      !urlObj.searchParams.get('campaignId') &&
      !isValidAmazonShortlinkDomain(url)
    ) {
      urlObj.searchParams.append('campaignId', campaignId);
      url = urlObj.toString();
    }
  }

  function appendCreatorConnectionsTag(message) {
    if (!tags.includes('Creator Connections')) {
      tags = [...tags, 'Creator Connections'];
    }
    creatorConnectionsCampaignState.message = message;
  }

  async function checkCampaignInUrl(url) {
    try {
      // if there is already a campaignId in the Url we won't suggest a new one.
      const queryString = url.split('?')[1];
      const searchParams = new URLSearchParams(queryString);
      const campaignId = searchParams.get('campaignId');
      // Ensure the campaignId in the url wasnt one we added
      if (campaignId && !creatorConnectionsCampaignState.suggestedCampaign) {
        appendCreatorConnectionsTag(
          $_(
            'CreateShortlink.creatorConnections.successMessage.userProvidedCampaignId',
            {
              default:
                "This link is for a Creator Connections campaign so we've added this label for you automatically.",
            },
          ),
        );
        creatorConnectionsCampaignState.userProvidedCampaignId = campaignId;
        creatorConnectionsCampaignState.userCampaignDecision = 'OPTED-IN';
        return;
      }

      const resp = await getCampaignForProductUrl(url);

      if (resp.status === 204) {
        // no campaign
        return;
      }

      const suggestedCampaign = resp.data.object;
      creatorConnectionsCampaignState.suggestedCampaign = suggestedCampaign;

      if (suggestedCampaign.creatorCampaignStatus === 'OPTED-IN') {
        // append and notify if they have already opted in
        appendCreatorConnectionsTag(
          $_('CreateShortlink.creatorConnnections.successMessage.priorOptIn', {
            default:
              "You've previously opted into a Campaign that also contains this ASIN so we’ve added this label for you and updated your link for tracking automatically.",
          }),
        );
        appendCampaignToUrl(suggestedCampaign.campaignId);
      } else {
        creatorConnectionsCampaignState.showInlineDialog = true;
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function rejectSuggestedCampaign() {
    creatorConnectionsCampaignState.showInlineDialog = false;
    creatorConnectionsCampaignState.userCampaignDecision = 'NOT-INTERESTED';
    creatorConnectionsCampaignState.showMoreDetails = false;
  }

  async function acceptSuggestedCampaign() {
    appendCreatorConnectionsTag(
      $_('CreateShortlink.creatorConnections.successMessage.optIn', {
        default:
          'We’ve updated your link and automatically added this label for easier link management now that you’ve opted into the Creator Connections campaign.',
      }),
    );

    creatorConnectionsCampaignState.userCampaignDecision = 'OPTED-IN';
    creatorConnectionsCampaignState.showInlineDialog = false;
    creatorConnectionsCampaignState.showMoreDetails = false;

    window.open(
      `https://affiliate-program.amazon.com/p/connect/requests?status=opportunity&sortBy=recommended_for_you&keyword=${encodeURIComponent(creatorConnectionsCampaignState.suggestedCampaign.campaignName)}&campaignStatuses=active%2Cpending`,
      '_blank',
    );

    appendCampaignToUrl(
      creatorConnectionsCampaignState.suggestedCampaign.campaignId,
    );
  }

  async function validateUrl(url) {
    const response = await client.post('/api/validate-url', {
      url,
    });

    if (response.status === 200) {
      onUrlValidationSuccess();

      // If the name is empty, suggest a name based on the URL
      const suggestedName = await generateLinkName(url);
      if (name === '' && suggestedName) {
        name = suggestedName;
        onNameInput();
      }
      if (ccEnabled) {
        checkCampaignInUrl(url);
      }
      return;
    }
    if (response.status === 400) {
      onUrlValidationError(response.data.error);
    }
  }

  async function generateLinkName(url) {
    const response = await client.get(
      `/api/preview?url=${encodeURIComponent(url)}`,
    );

    if (response.status === 200) {
      const suggestedName = response.data.object?.title;

      if (suggestedName) {
        return suggestedName.length > 75
          ? `${suggestedName.substring(0, 75)}...`
          : suggestedName;
      }
    }

    return '';
  }

  function displayError(err) {
    return (
      {
        'url must be part of the request': $_('CreateShortlink.nullUrl', {
          default: '',
        }),
        'You already have a shortlink with this name': $_(
          'CreateShortlink.duplicateName',
          {
            default: 'You already have a shortlink with this name',
          },
        ),
        'url does not have a supported amazon domain': $_(
          'CreateShortlink.invalidDomain',
          {
            default: 'URL does not have a supported Amazon domain',
          },
        ),
        'the tag in the url must end with the characters "1-##"': $_(
          'CreateShortlink.invalidTagEnding',
          {
            default:
              'The tag in the url must end with the characters "1-20" or "1-21"',
          },
        ),
        'unsupported amazon shortlink': $_(
          'CreateShortlink.unsupportedAmazonShortlink',
          {
            default: 'The provided amzn.to link is invalid',
          },
        ),
        'name must be between 1 and 100 characters': $_(
          'CreateShortlink.invalidNameLength',
          {
            default: 'Name must be between 1 and 100 characters',
          },
        ),
      }[err] ??
      $_('CreateShortlink.default', {
        default: 'An unexpected error occurred, try again',
      })
    );
  }

  const tagsAutocomplete = debounce(async function tagsAutocomplete(search) {
    try {
      const response = await client.get(`/api/get-tags?search=${search}`);
      return response.data.objects;
    } catch (e) {
      return [];
    }
  }, 500);

  function getStatus(state) {
    // ToDo: Clean up by adding icon here`
    const status = {
      NOT_STARTED: {
        status: 'Not Started',
        btnClass: 'bg-campaignStatusNotStarted',
      },
      LIVE: {
        status: 'Live',
        btnClass: 'bg-campaignStatusLive',
      },
    };
    return status[state] ?? status.LIVE;
  }
</script>

<Modal
  autoclose
  bind:open={creatorConnectionsCampaignState.showMoreDetails}
  size="sm"
>
  <div class="p-4">
    <div>
      <header>
        <Heading class="mb-2">
          {$_('CreateShortlink.creatorConnections.detailsModal.heading', {
            default: 'Campaign Details',
          })}
        </Heading>
        <div class="mb-2 flex items-center gap-1">
          {#if getStatus(creatorConnectionsCampaignState.suggestedCampaign.campaignStatus).status === 'Not Started'}
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_2060_7739)">
                <g clip-path="url(#clip1_2060_7739)">
                  <path
                    d="M2.89783 8H1.36719"
                    stroke="#FA8C16"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4.3883 4.38719L3.30664 3.30553"
                    stroke="#FA8C16"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4.3883 11.6122L3.30664 12.694"
                    stroke="#FA8C16"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M13.1021 8H14.6327"
                    stroke="#FA8C16"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M11.6123 11.6122L12.694 12.694"
                    stroke="#FA8C16"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 13.102V14.6327"
                    stroke="#FA8C16"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M11.6123 4.38719L12.694 3.30552"
                    stroke="#FA8C16"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M8 2.898V1.36735"
                    stroke="#FA8C16"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g>
              </g>
              <defs>
                <clipPath id="clip0_2060_7739">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
                <clipPath id="clip1_2060_7739">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
          {:else if getStatus(creatorConnectionsCampaignState.suggestedCampaign.campaignStatus).status === 'Live'}
            <svg
              class="h-6 w-6 text-green-500 dark:text-green-400"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m8.032 12 1.984 1.984 4.96-4.96m4.55 5.272.893-.893a1.984 1.984 0 0 0 0-2.806l-.893-.893a1.984 1.984 0 0 1-.581-1.403V7.04a1.984 1.984 0 0 0-1.984-1.984h-1.262a1.983 1.983 0 0 1-1.403-.581l-.893-.893a1.984 1.984 0 0 0-2.806 0l-.893.893a1.984 1.984 0 0 1-1.403.581H7.04A1.984 1.984 0 0 0 5.055 7.04v1.262c0 .527-.209 1.031-.581 1.403l-.893.893a1.984 1.984 0 0 0 0 2.806l.893.893c.372.372.581.876.581 1.403v1.262a1.984 1.984 0 0 0 1.984 1.984h1.262c.527 0 1.031.209 1.403.581l.893.893a1.984 1.984 0 0 0 2.806 0l.893-.893a1.985 1.985 0 0 1 1.403-.581h1.262a1.984 1.984 0 0 0 1.984-1.984V15.7c0-.527.209-1.031.581-1.403Z"
              />
            </svg>
          {/if}
          <p>
            {getStatus(
              creatorConnectionsCampaignState.suggestedCampaign.campaignStatus,
            ).status}
          </p>
        </div>
      </header>
      <Listgroup class="mb-6">
        <ListgroupItem>
          <div class="flex justify-between">
            <div class="font-normal text-gray-500">
              {$_(
                'CreateShortlink.creatorConnections.detailsModal.table.startDate',
                {
                  default: 'Start Date',
                },
              )}
            </div>
            <div class="text-gray-900">
              {new Date(
                creatorConnectionsCampaignState.suggestedCampaign.campaignStartDate,
              ).toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric',
              })}
            </div>
          </div>
        </ListgroupItem>
        <ListgroupItem>
          <div class="flex justify-between">
            <div class="font-normal text-gray-500">
              {$_(
                'CreateShortlink.creatorConnections.detailsModal.table.endDate',
                {
                  default: 'End Date',
                },
              )}
            </div>
            <div class="text-gray-900">
              {new Date(
                creatorConnectionsCampaignState.suggestedCampaign.campaignEndDate,
              ).toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric',
              })}
            </div>
          </div>
        </ListgroupItem>
        <ListgroupItem>
          <div class="flex justify-between">
            <div class="font-normal text-gray-500">
              {$_(
                'CreateShortlink.creatorConnections.detailsModal.table.commissionRate',
                {
                  default: 'Commission Rate',
                },
              )}
            </div>
            <div class="text-gray-900">
              {creatorConnectionsCampaignState.suggestedCampaign
                .commissionRate}%
            </div>
          </div>
        </ListgroupItem>
        <ListgroupItem>
          <div class="flex justify-between">
            <div class="font-normal text-gray-500">
              {$_(
                'CreateShortlink.creatorConnections.detailsModal.table.campaignBudget',
                {
                  default: 'Campaign Budget',
                },
              )}
            </div>
            <div class="text-gray-900">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(
                creatorConnectionsCampaignState.suggestedCampaign
                  .campaignBudget,
              )}
            </div>
          </div>
        </ListgroupItem>
      </Listgroup>
    </div>
    <div class="mb-6 flex items-center gap-2">
      <Button on:click={acceptSuggestedCampaign}>
        <PlusSolid size="sm" class="mr-2" />
        {$_('CreateShortlink.creatorConnections.detailsModal.acceptButton', {
          default: 'Opt In',
        })}
      </Button>
      <Button color="alternative">
        {$_(
          'CreateShortlink.creatorConnections.detailsModal.notInterestedButton',
          {
            default: 'Not Interested',
          },
        )}
      </Button>
    </div>
    <p class="text-sm">
      {$_('CreateShortlink.creatorConnections.detailsModal.acceptString-1', {
        default:
          'By clicking accept, you agree to this campaign as governed by the',
      })}

      <a
        href="https://affiliate-program.amazon.com/p/connect/help/agreement"
        class="underline"
      >
        {$_(
          'CreateShortlink.creatorConnections.detailsModal.affiliateProgramAgreementLink',
          {
            default: 'Creator Connections Associates Policy',
          },
        )}
      </a>
      {$_('CreateShortlink.creatorConnections.detailsModal.acceptString-2', {
        default:
          ', and the campaign requirements outlined in campaign brief. Unavoidable disclosure is required on each piece of content posted for this campaign.',
      })}
    </p>
  </div>
</Modal>
<Card class="mb-4 w-full max-w-none">
  <h2 class="mb-4 text-xl font-bold text-gray-900">
    {$_('CreateShortlink.createNewShortlink', {
      default: 'Create New Shortlink',
    })}
  </h2>
  <form class="space-y-4 lg:space-y-6" on:submit|preventDefault={onCreateLink}>
    <div class="w-full">
      <Label class="font-bold">
        {$_('CreateShortlink.destination', {
          default: 'Destination',
        })}
        <Input
          type="url"
          id="url-input"
          bind:value={url}
          on:input={onUrlInput}
          on:blur={onUrlBlur}
          placeholder={urlPlaceholder}
          class={cls('mt-2 font-normal', urlValidationState.inputClass)}
          required
        />
      </Label>
      <div>
        <span
          class={cls('relative top-2 text-sm', urlValidationState.outputClass)}
        >
          {urlValidationState.message}
        </span>
      </div>
      {#if creatorConnectionsCampaignState.showInlineDialog}
        <div transition:slide class="mb-4 mt-2">
          <p class="text-sm font-light text-gray-400">
            {$_('CreateShortlink.creatorConnections.inlineDialog.text', {
              default:
                "There's a Creator Connections campaign for this ASIN - opt-in now to start earning commissions?",
            })}
          </p>
          <div class="mt-2">
            <Button size="sm" on:click={acceptSuggestedCampaign}>
              {$_('CreateShortlink.creatorConnections.inlineDialog.confirm', {
                default: 'Yes',
              })}
            </Button>
            <Button
              class="ml-1"
              size="sm"
              outline
              on:click={rejectSuggestedCampaign}
            >
              {$_('CreateShortlink.creatorConnections.inlineDialog.deny', {
                default: 'No',
              })}
            </Button>
            <Button
              class="ml-1"
              size="sm"
              color="light"
              on:click={() => {
                creatorConnectionsCampaignState.showMoreDetails = true;
              }}
            >
              {$_(
                'CreateShortlink.creatorConnections.inlineDialog.moreDetails',
                {
                  default: 'More Details',
                },
              )}
            </Button>
          </div>
        </div>
      {/if}
    </div>
    <div>
      <Label class="font-bold">
        {$_('CreateShortlink.linkName', {
          default: 'Link Name',
        })}
        <Input
          id="name-input"
          bind:value={name}
          on:input={onNameInput}
          placeholder={namePlaceholder}
          class={cls('mt-2 font-normal', nameValidationState.inputClass)}
          required
        />
      </Label>
      <span
        class={cls('relative top-2 text-sm', nameValidationState.outputClass)}
      >
        {nameValidationState.message}
      </span>
    </div>
    <div class="w-full">
      <Tags
        placeholder={tags.length === 0 &&
          $_('CreateShortlink.labelsPlaceholder', {
            default: 'E.g. Spring Sale, Fashion',
          })}
        addKeys={[9, 13, 188]}
        allowBlur
        allowPaste
        onlyUnique
        autoComplete={tagsAutocomplete}
        autoCompleteKey={'tag_name'}
        autoCompleteShowKey={'tag_name'}
        autoCompleteMarkupKey={'tag_name'}
        maxTags={10}
        maxChars={100}
        bind:tags
      />
      {#if creatorConnectionsCampaignState.message}
        <div
          class="mt-2 flex items-center gap-2 text-sm font-normal text-btnInputSuccess"
        >
          <StarHalfStrokeSolid />
          <p>{creatorConnectionsCampaignState.message}</p>
        </div>
      {/if}
    </div>
    <div>
      <Button
        type="submit"
        disabled={!urlValidationState.isValid ||
          !nameValidationState.isValid ||
          isCreatingShortlink}
        class="mt-4"
      >
        <svg
          class="-ml-1 mr-3 h-5 w-5 animate-spin {isCreatingShortlink
            ? ''
            : 'hidden'}"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <PlusSolid
          size="sm"
          class="mr-2 {isCreatingShortlink ? 'hidden' : ''}"
        />
        {#if isCreatingShortlink}
          {$_('CreateShortlink.cta.creating', {
            default: 'Creating...',
          })}
        {:else if tags.includes('Creator Connections')}
          {$_('CreateShortlink.cta.amazon-cc.createShortlink', {
            default: 'Create Creator Connections Shortlink',
          })}
        {:else}
          {$_('CreateShortlink.cta.createShortlink', {
            default: 'Create Shortlink',
          })}
        {/if}
      </Button>
    </div>
  </form>
  <StickyHelp {isShowingHelpModal} />
</Card>
